import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { useToast } from './use-toast';
import { supabase } from '@/integrations/supabase/client';

type FunctionErrorBody = { error?: string; details?: string };

const functionErrorMessage = async (
  error: unknown,
  data: unknown,
  fallback: string,
): Promise<string> => {
  let body = data as FunctionErrorBody | string | null;
  const context = (error as { context?: Response })?.context;
  if ((body == null || typeof body !== 'object') && context && typeof context.json === 'function') {
    try {
      body = (await context.clone().json()) as FunctionErrorBody;
    } catch {
      try {
        const text = await context.clone().text();
        if (text?.trim()) return text.slice(0, 400);
      } catch {
        // ignore unreadable bodies
      }
    }
  }
  if (body && typeof body === 'object') {
    if (body.details) return String(body.details);
    if (body.error) return String(body.error);
  }
  if (error instanceof Error && error.message && !error.message.includes('non-2xx')) {
    return error.message;
  }
  return fallback;
};

export type VoiceProvider = 'telnyx' | 'elevenlabs';

export interface TelnyxAgent {
  id: string;
  user_id: string;
  name: string;
  grade?: string;
  provider: string;
  telnyx_assistant_id: string | null;
  elevenlabs_agent_id: string | null; // legacy, read-only (demo embeds)
  voice_provider: VoiceProvider | null;
  description: string | null;
  industry: string | null;
  use_case: string | null;
  system_prompt: string;
  first_message: string | null;
  voice_id: string;
  voice_name: string | null;
  tts_model: string;
  llm_model: string;
  language: string;
  max_duration_seconds: number;
  phone_number_id?: string | null;
  knowledge_base_ids: any[];
  tools_config: any[];
  conversation_config: any;
  is_active: boolean;
  is_template: boolean;
  created_at: string;
  updated_at: string;
}

// Mirrors useElevenLabsAgents, but reads/writes the canonical `agents` table
// directly and drives the `telnyx-manage-agent` edge function (same
// action: create|update|delete request/response contract as
// elevenlabs-manage-agent). No React Query cache keys are used here, so there
// is nothing to clash with the legacy hook during the transition.
export const useTelnyxAgents = () => {
  const [agents, setAgents] = useState<TelnyxAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { toast } = useToast();

  const fetchAgents = useCallback(async () => {
    if (!user) return;
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('agents')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAgents((data as any[]) || []);
    } catch (error: any) {
      console.error('Error fetching agents:', error);
      toast({ title: 'Error', description: 'Failed to load agents', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [user, toast]);

  useEffect(() => {
    if (user) fetchAgents();
  }, [user, fetchAgents]);

  const createAgent = async (agentData: {
    name: string;
    description?: string;
    industry?: string;
    use_case?: string;
    grade?: string;
    system_prompt: string;
    first_message?: string;
    voice_id: string;
    voice_name?: string;
    voice_provider?: VoiceProvider;
    llm_model?: string;
    language?: string;
    tools_config?: any[];
    max_duration_seconds?: number;
    phone_number_id?: string;
  }) => {
    try {
      const { data, error } = await supabase.functions.invoke('telnyx-manage-agent', {
        body: { action: 'create', provider: 'telnyx', ...agentData },
      });

      if (error) throw new Error(await functionErrorMessage(error, data, 'Failed to create agent'));
      if (data?.error) throw new Error(await functionErrorMessage(error, data, 'Failed to create agent'));
      setAgents(prev => [data, ...prev]);
      toast({ title: 'Success', description: 'Agent created successfully!' });
      if (data?.phone_assignment && data.phone_assignment.assigned === false) {
        toast({
          title: 'Number not bound yet',
          description: data.phone_assignment.reason === 'phone_not_active_telnyx'
            ? 'The number is still pending Telnyx KYC. Refresh it in Settings, then re-save this agent.'
            : `Inbound/outbound routing was not attached (${data.phone_assignment.reason || 'unknown'}).`,
          variant: 'destructive',
        });
      }
      return data;
    } catch (error: any) {
      console.error('Error creating agent:', error);
      toast({
        title: 'Error',
        description: error?.message || 'Failed to create agent',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const updateAgent = async (agentId: string, updates: Partial<TelnyxAgent>) => {
    try {
      const { data, error } = await supabase.functions.invoke('telnyx-manage-agent', {
        body: { action: 'update', agent_id: agentId, updates },
      });

      if (error) throw error;
      setAgents(prev => prev.map(a => (a.id === agentId ? data : a)));
      toast({ title: 'Success', description: 'Agent updated successfully!' });
      if (data?.phone_assignment && data.phone_assignment.assigned === false) {
        toast({
          title: 'Number not bound yet',
          description: `Inbound/outbound routing was not attached (${data.phone_assignment.reason || 'unknown'}).`,
          variant: 'destructive',
        });
      }
      return data;
    } catch (error: any) {
      console.error('Error updating agent:', error);
      toast({ title: 'Error', description: 'Failed to update agent', variant: 'destructive' });
      throw error;
    }
  };

  const deleteAgent = async (agentId: string) => {
    try {
      const { error } = await supabase.functions.invoke('telnyx-manage-agent', {
        body: { action: 'delete', agent_id: agentId },
      });

      if (error) throw error;
      setAgents(prev => prev.filter(a => a.id !== agentId));
      toast({ title: 'Success', description: 'Agent deleted successfully!' });
    } catch (error: any) {
      console.error('Error deleting agent:', error);
      toast({ title: 'Error', description: 'Failed to delete agent', variant: 'destructive' });
      throw error;
    }
  };

  const duplicateAgent = async (agentId: string) => {
    const original = agents.find(a => a.id === agentId);
    if (!original) return;

    return createAgent({
      name: `${original.name} (Copy)`,
      description: original.description || '',
      industry: original.industry || '',
      use_case: original.use_case || '',
      system_prompt: original.system_prompt,
      first_message: original.first_message || '',
      voice_id: original.voice_id,
      voice_name: original.voice_name || '',
      voice_provider: original.voice_provider || 'telnyx',
      llm_model: original.llm_model,
      language: original.language,
      tools_config: original.tools_config,
      max_duration_seconds: original.max_duration_seconds,
    });
  };

  return {
    agents,
    loading,
    createAgent,
    updateAgent,
    deleteAgent,
    duplicateAgent,
    refetch: fetchAgents,
  };
};
